import React from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'semantic-ui-react';
import { formatUrl } from '@plone/volto/helpers/Api/Api';

import { formatTempo, type EpisodioItem } from './helpers';

/** Um episódio como o `@podcast-feed` devolve. */
export interface EpisodioDoFeed {
  id: string;
  titulo: string;
  data: string | null;
  descricao: string;
  audio: string;
  tipo: string | null;
  duracao: number | null;
  imagem: string | null;
  link: string | null;
}

interface RespostaDoFeed {
  status:
    | 'ok'
    | 'invalid'
    | 'blocked'
    | 'timeout'
    | 'too_large'
    | 'parse_error'
    | 'empty'
    | 'error';
  title: string | null;
  image: string | null;
  episodes: EpisodioDoFeed[];
  total: number;
  cached: boolean;
  message?: string;
}

interface SeletorDeEpisodiosProps {
  /** URL do feed, como está gravada no bloco. */
  feed?: string;
  /** Episódios já escolhidos, para não oferecer o mesmo duas vezes. */
  escolhidos?: EpisodioItem[];
  onAdd: (episodio: EpisodioItem) => void;
}

const LIMITE = 20;

/**
 * Lista os episódios do feed do podcast para o editor escolher.
 *
 * Existe porque nenhuma das plataformas do card (Spotify, Deezer, Amazon
 * Music) entrega o ARQUIVO de áudio — o oEmbed do Spotify devolve título e
 * capa, a API do Deezer devolve metadados mais um token que só o player deles
 * consome, e a Amazon não publica nada. O RSS é a origem que alimenta as três,
 * e é onde está o `<enclosure>`: o MP3 público que toca no `<audio>` do card.
 *
 * O que o editor escolhe é **gravado no bloco**, não buscado a cada visita. A
 * página pública do MPF não deve depender da disponibilidade de um servidor de
 * terceiro no seu tempo de resposta, nem mudar de conteúdo sozinha depois de
 * publicada — mudança em conteúdo publicado precisa de autoria e trilha.
 */
export const SeletorDeEpisodios: React.FC<SeletorDeEpisodiosProps> = ({
  feed,
  escolhidos,
  onAdd,
}) => {
  /* O token PRECISA ir no cabeçalho `Authorization`. O plugin JWT do
     plone.restapi (`pas/plugin.py#extractCredentials`) só olha para
     `Authorization: Bearer …` — nunca para o cookie `auth_token`. Quem
     converte o cookie em cabeçalho é o SSR do Volto; um `fetch` do navegador
     com `credentials: 'include'` e mais nada leva 401 do backend. */
  const token = useSelector((state: any) => state.userSession?.token);

  const [carregando, setCarregando] = React.useState(false);
  const [resposta, setResposta] = React.useState<RespostaDoFeed | null>(null);
  const [erro, setErro] = React.useState<string | null>(null);

  const alvo = (feed ?? '').trim();

  // Uma busca em voo continua válida enquanto o campo não mudar. Se o editor
  // trocar o feed no meio, o resultado que chegar é de outro feed.
  const buscandoPara = React.useRef<string | null>(null);

  React.useEffect(() => {
    setResposta(null);
    setErro(null);
  }, [alvo]);

  const buscar = React.useCallback(async () => {
    if (!alvo) return;

    buscandoPara.current = alvo;
    setCarregando(true);
    setErro(null);

    try {
      const endpoint = formatUrl(
        `/@podcast-feed?url=${encodeURIComponent(alvo)}&limit=${LIMITE}`,
      );
      const r = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (buscandoPara.current !== alvo) return;

      if (!r.ok) {
        setErro('Não foi possível ler o feed agora. Tente de novo mais tarde.');
        return;
      }

      const dados = (await r.json()) as RespostaDoFeed;
      if (buscandoPara.current !== alvo) return;

      setResposta(dados);
      // A mensagem vem do servidor, que é quem sabe por que não deu — endereço
      // recusado, timeout e XML inválido pedem orientações diferentes.
      if (dados.status !== 'ok') setErro(dados.message ?? null);
    } catch {
      if (buscandoPara.current === alvo) {
        setErro('Não foi possível ler o feed agora. Tente de novo mais tarde.');
      }
    } finally {
      if (buscandoPara.current === alvo) setCarregando(false);
    }
  }, [alvo, token]);

  const jaEscolhidos = React.useMemo(
    () => new Set((escolhidos ?? []).map((e) => e['@id'])),
    [escolhidos],
  );

  if (!alvo) {
    return (
      <p className="mpf-podcast-feed__ajuda">
        Preencha o endereço do feed RSS acima para escolher episódios da lista,
        em vez de digitar um a um.
      </p>
    );
  }

  return (
    <div className="mpf-podcast-feed">
      <Button
        type="button"
        primary
        compact
        loading={carregando}
        disabled={carregando}
        onClick={buscar}
      >
        {resposta ? 'Buscar de novo' : 'Buscar episódios'}
      </Button>

      {erro && (
        <p className="mpf-podcast-feed__erro" role="status">
          {erro}
        </p>
      )}

      {resposta?.status === 'ok' && (
        <>
          <p className="mpf-podcast-feed__resumo" role="status">
            {resposta.title} — {resposta.episodes.length} de {resposta.total}{' '}
            episódios
            {resposta.cached && ' (em cache)'}
          </p>

          <ul className="mpf-podcast-feed__lista">
            {resposta.episodes.map((episodio) => {
              const adicionado = jaEscolhidos.has(episodio.id);
              return (
                <li key={episodio.id} className="mpf-podcast-feed__item">
                  <span className="mpf-podcast-feed__titulo">
                    {episodio.titulo || 'Sem título'}
                  </span>
                  <span className="mpf-podcast-feed__meta">
                    {episodio.data ? episodio.data.slice(0, 10) : 'sem data'}
                    {episodio.duracao
                      ? ` · ${formatTempo(episodio.duracao)}`
                      : ''}
                  </span>
                  <Button
                    type="button"
                    basic
                    compact
                    size="tiny"
                    disabled={adicionado}
                    onClick={() =>
                      onAdd({
                        '@id': episodio.id,
                        data: episodio.data ?? undefined,
                        titulo: episodio.titulo,
                        descricao: episodio.descricao,
                        audio: episodio.audio,
                        link: episodio.link ?? undefined,
                      })
                    }
                  >
                    {adicionado ? 'Já está na faixa' : 'Adicionar'}
                  </Button>
                </li>
              );
            })}
          </ul>

          <p className="mpf-podcast-feed__ajuda">
            A plataforma (Spotify, Deezer, Amazon Music) não vem no feed —
            escolha a de cada episódio no formulário acima.
          </p>
        </>
      )}
    </div>
  );
};

export default SeletorDeEpisodios;
